/**
 * SearchExecutions slice — generic, filter-driven execution search.
 *
 * searchExecutions(input): POST /executions/search via beaconApi; stores the raw
 * SearchExecutions summaries + total count. Callers supply the
 * SearchExecutionsInput (filters/pagination/sort). The bulk execution search
 * page uses it with filters.executionIds = [queried ids].
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ApiExecutionSummary, SearchExecutionsInput } from './searchExecutionsApi';
import { beaconApi } from '../../../services/beaconApi';
import { RootState } from '../../store';

export interface SearchExecutionsState {
  executions: ApiExecutionSummary[];
  total: number;
  loading: boolean;
  error: string | null;
  currentRequestId: string | null;
  /** Last search input; re-dispatched on region change. */
  lastInput: SearchExecutionsInput | null;
}

export const searchExecutionsInitialState: SearchExecutionsState = {
  executions: [],
  total: 0,
  loading: false,
  error: null,
  currentRequestId: null,
  lastInput: null,
};

interface FetchResult {
  executions: ApiExecutionSummary[];
  total: number;
}

export const searchExecutions = createAsyncThunk<
  FetchResult, SearchExecutionsInput, { state: RootState; rejectValue: string }
>('searchExecutions/run', async (input, { getState, rejectWithValue }) => {
  const { apiBase } = getState().env;
  try {
    const response = await beaconApi.searchExecutions(input, apiBase);
    return { executions: response.executions ?? [], total: response.totalExecutionsCount ?? 0 };
  } catch (err: any) {
    return rejectWithValue(err?.message || 'Failed to search executions');
  }
});

const searchExecutionsSlice = createSlice({
  name: 'searchExecutions',
  initialState: searchExecutionsInitialState,
  reducers: {
    clearSearchExecutions: () => searchExecutionsInitialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchExecutions.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentRequestId = action.meta.requestId;
        state.lastInput = action.meta.arg;
      })
      .addCase(searchExecutions.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.executions = action.payload.executions;
        state.total = action.payload.total;
      })
      .addCase(searchExecutions.rejected, (state, action) => {
        if (action.meta.requestId !== state.currentRequestId) return;
        state.loading = false;
        state.error = action.payload || action.error.message || 'Failed to search executions';
      });
  },
});

export const { clearSearchExecutions } = searchExecutionsSlice.actions;
export default searchExecutionsSlice.reducer;
